import { useUserInfo } from "@/api/user";
import { config } from "@/config";
import { LDContext, LDProvider } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";

export const flagValues = {
  onboarding: "onboarding",
  inbox: "inbox",
  audioTranscription: "audioTranscription",
  billing: "billing",
  canvasCentricApp: "canvasCentricApp",
  canvasCentricAppAdminToggle: "canvasCentricAppAdminToggle",
  notionLikeEditor: "notionLikeEditor",
  maintenanceMode: "maintenanceMode",
  extremeReasoningEffort: "extremeReasoningEffort",
  // LD key: terminal-widget — gates the Terminal widget in the canvas widget picker.
  terminalWidget: "terminalWidget",
  // LD key: notifications — per-org gate for the Liveblocks notifications feature
  // (the bell + tool-confirmation auto-delete). Matches the backend flag key.
  notifications: "notifications",
} as const;

export type Flags = {
  [K in keyof typeof flagValues]: boolean;
};

export function LaunchDarklyProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: userInfo } = useUserInfo();

  const launchDarklyContext = useMemo(() => {
    if (userInfo) {
      const context: LDContext = {
        kind: "multi",
        user: {
          key: userInfo?.user?.id,
          name: userInfo?.user?.name,
          email: userInfo?.user?.email,
          created_at: userInfo?.user?.created_at,
        },
      };

      if (userInfo?.organization) {
        context.organization = {
          key: userInfo?.organization?.id,
          name: userInfo?.organization?.name,
        };
      }

      return context;
    }

    return undefined;
  }, [userInfo]);

  const relayHost = config.launchDarkly.relayHost;

  return (
    <LDProvider
      clientSideID={config.launchDarkly.clientSideId}
      context={launchDarklyContext}
      deferInitialization
      options={
        relayHost
          ? {
              baseUrl: relayHost,
              streamUrl: relayHost,
              eventsUrl: relayHost,
            }
          : undefined
      }
    >
      {children}
    </LDProvider>
  );
}
